import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useForm,
    useAuth,
    useFetch,
    useResource,
    useGrid,
    useModal,
    useStore,
    useToast,
    usePopup,
    usePage,
} from "@/comn/hooks"; // hook
import {
    BASE,
    URLS,
    APIS,
    SF_BTCH_EXCUTN_FORM,
    SG_BTCH_EXCUTN_PRMT_LIST,
} from "@/tz/go/egaz/osci/ptl/btch/excutn/services/OsciBtchExcutnMgmtService";

/*
 * Managing batch runs Details
 * !== 배치실행관리 상세 ==!
 */
export const OSCI0112010S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const { btchId } = useParams();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0088" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Managing batch runs Form !== 배치실행관리 Form ==!
        btchExcutnForm: useForm({
            defaultSchema: SF_BTCH_EXCUTN_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        btchExcutnPrmtList: useGrid({
            defaultSchema: SG_BTCH_EXCUTN_PRMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getBtchExcutnInfo: useFetch({
            api: () => APIS.getBtchExcutnInfo(btchId),
            onSuccess: (data: any) => {
                // 데이터가 없을 경우
                if (!data.excutnInfo.content) {
                    modal.openModal({ content: "msg.osc.00050" }); // No data found. !== 해당 데이터가 없습니다. ==!

                    navigate(URLS.osci0112009q);
                    return;
                }
                form.btchExcutnForm.setValues(data.excutnInfo.content);
                grid.btchExcutnPrmtList.setData({ content: data.excutnInfo.content.ptliExcutnPrmtList });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.osci0112009q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // Batch execution !== 배치 실행 ==!
        executeBtch: useFetch({
            api: (data) => APIS.executeBtch({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00112", // Successfully executed. !== 정상적으로 실행되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.osci0112009q);
                    },
                    close: false,
                });
            },
            onError: (error: any) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
        // Pause Batch !== 배치 중지 ==!
        pauseBtch: useFetch({
            api: (data) => APIS.pauseBtch({ ...data }),
            onSuccess: (data) => {
                // ExitCode definition = A: FAIL, B: SUCCESS
                const exitCode = data.exitInfo.content.exitCode;
                const content = exitCode === "A" ? "msg.osc.00121" : "msg.osc.00119";

                modal.openModal({
                    content: content,
                    onConfirm: () => {},
                    close: false,
                });
            },
            onError: (error: any) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
        // Resume Batch !== 배치 재실행 ==!
        resumeBtch: useFetch({
            api: (data) => APIS.resumeBtch({ ...data }),
            onSuccess: (data) => {
                // ExitCode definition = A: FAIL, B: SUCCESS
                const exitCode = data.exitInfo.content.exitCode;
                const content = exitCode === "A" ? "msg.osc.00122" : "msg.osc.00112";

                modal.openModal({
                    content: content,
                    onConfirm: () => {},
                    close: false,
                });
            },
            onError: (error: any) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onListBtnClick: () => navigate(URLS.osci0112009q),
        // on Execution Button Click Event Handler  !== 배치 실행 ==!
        onExcutnBtnClick: () => {
            if (!comnUtils.isEmpty(form.btchExcutnForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.btchExcutnForm.handleSubmit(
                () => {
                    const data = form.btchExcutnForm.getValues();
                    const excutnPrmtList = grid.btchExcutnPrmtList.getData();

                    // Setting batch execution parameter list !== 배치 실행 파라미터 목록 세팅 ==!
                    data.excutnPrmtList = excutnPrmtList;

                    modal.openModal({
                        content: "msg.osc.00180", // Do you want to execute? !== 실행 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.executeBtch.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_btchExcutnPrmtList: {
            edit: {
                prmtNm: () => {
                    return (
                        <FormControl
                            {...form.btchExcutnForm.schema.prmtNm}
                            controlSize={10}
                            edit={false}
                            value={"requestTarget"}
                        />
                    );
                },
            },
        },
    };

    useEffect(() => {
        fetch.getBtchExcutnInfo.fetch();
        form.btchExcutnForm.setEditable(false);
        grid.btchExcutnPrmtList.setEdit("column", "btchPrmtNm", false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnForm.schema.btchId} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnForm.schema.btchNm} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnForm.schema.bsopClsfCd} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_BTCH_PRMT_LIST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.btchExcutnPrmtList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0112009q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        variant="primary"
                        onClick={() => {
                            handler.onExcutnBtnClick();
                        }}
                    >
                        {t("B_RUN_ONCE")}
                    </Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
